import { formatNumber } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { UserBuying } from 'src/app/models/acquista-servizi-models/user-buying';
import { Products } from 'src/app/models/Products';
import { UserIdentity } from 'src/app/models/user-identity';
import { map, startWith } from 'rxjs/operators';
import { OrdersService } from 'src/app/core/services/acquista-servizi/orders/orders.service';
import { Helpers } from 'src/app/models/Helpers/helpers';
import { Loader } from 'src/app/models/Helpers/loader';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserBuyingsService } from 'src/app/core/services/user-buyings/user-buyings.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { DialogService } from 'src/app/core/services/dialog.service';

@Component({
  selector: 'app-subscriptions-dialog',
  templateUrl: './subscriptions-dialog.component.html',
  styleUrls: ['./subscriptions-dialog.component.scss'],
})
export class SubscriptionsDialogComponent implements OnInit {

  form: FormGroup;
  subscriptionsForm: FormGroup;
  data: {
    user: UserIdentity,
    userGuid: string,
    userBuyings: UserBuying[],
    subscriptionTypes: Products[]
  };

  subscriptionsNames: string[];
  filteredSubscriptionsNames: Observable<string[]>;

  loader = new Loader();

  constructor(
    private formBuilder: FormBuilder,
    private ordersService: OrdersService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<any>,
    private dialogService: DialogService,
    private userBuyingsService: UserBuyingsService,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) { }

  ngOnInit(): void {
    this.data = this.dialogData;
    this.subscriptionsNames = this.data.subscriptionTypes.map(product => product.name);

    this.form = this.formBuilder.group({
      product: ['', Validators.required],
      price: ['', [
        Validators.required,
        Validators.pattern('^-?[0-9]\\d*(\\,\\d*)?$')
      ]],
      type: [{ value: '', disabled: true }, Validators.required],
      life: '',
      insertingDate: ['', Validators.required],
      expirationDate: ['', Validators.required]
    });

    this.form.get('product').valueChanges
      .subscribe(
        (productName: string) => {
          const productData = this.data.subscriptionTypes.find(product => product.name === productName);
          if (productData) {
            this.form.get('price').setValue(productData ? formatNumber(productData.price, 'it', '1.2-2') : '');
            this.form.get('type').setValue(productData?.isSubscription ? 'Abbonamento' : 'Altro');
            this.form.get('life').setValue(productData?.credits ? `${productData.credits} giorni` : '');
            const today = new Date();
            this.form.get('insertingDate').setValue(today);
            let expiration = new Date();
            productData?.credits ? expiration.setDate(expiration.getDate() + productData.credits) : expiration = null;
            this.form.get('expirationDate').setValue(expiration ? expiration : '');
          }
        }
      );

    this.productSelectionListener();

    this.subscriptionsForm = this.formBuilder.group(
      { dates: this.formBuilder.array([]) }
    );

    this.data.userBuyings = this.data.userBuyings.reverse();

    this.data.userBuyings.forEach(item => this.dates.push(this.createFormGroup(item)))
  }


  productSelectionListener() {
    this.filteredSubscriptionsNames = this.form.get('product').valueChanges
      .pipe(
        startWith(''),
        map((product: string) => {
          if (product.length >= 2) {
            const filterValue = product.toLowerCase();

            return this.subscriptionsNames
              .filter(value => value.toLowerCase().startsWith(filterValue));
          }
        })
      );
  }

  assignSubscription() {
    this.loader.show();

    const formValues = this.form.value;
    const productData = this.data.subscriptionTypes.find(product => product.name === formValues.product);

    const orderData = {
      userId: this.data.userGuid,
      productId: productData.id,
      totalPrice: Number((formValues.price as string).replace(',', '.')),
      paymentMethod: 'B',
      insertingDate: Helpers.convertDateToLocalFormat(new Date(formValues.insertingDate)),
      expirationDate: Helpers.convertDateToLocalFormat(new Date(formValues.expirationDate)),
      isSubscription: productData.isSubscription,
      credits: productData.credits ? productData.credits : 0,
      userData: {},
      product: {
        amount: 1,
        productId: productData.id,
        productName: productData.name,
        productPriceCadauno: productData.price,
        productIvaCadauno: productData.iva,
        totalPrice: Number((formValues.price as string).replace(',', '.'))
      }
    };

    this.ordersService.postHandmadeOrder(orderData)
      .subscribe(
        () => {
          this.loader.hide();
          this.dialogRef.close(true);
        },
        error => {
          this.loader.hide();
          this.snackBar.open('Assegnazione abbonamento non riuscita. Riprova', '', {
            duration: 4000,
            verticalPosition: 'bottom',
            panelClass: ['failure-snackbar'],
          });
          console.log('ERROR IN GESTIONE ABBONAMENTI -> POST ORDER', error);
        }
      );
  }

  resetForm() {
    this.form.reset({
      product: '',
      price: '',
      type: '',
      life: '',
      insertingDate: '',
      expirationDate: ''
    });
  }

  createFormGroup(userBuying: UserBuying): FormGroup {
    return this.formBuilder.group({
      purchaseDate: userBuying.purchaseDate,
      expirationDate: userBuying.expirationDate,
      id: userBuying.id
    });
  }

  get dates(): FormArray {
    return this.subscriptionsForm.get("dates") as FormArray
  }

  hasPurchaseDateChanged(index: number): boolean {
    const purchaseDateControl = this.dates.get(index.toString()).get("purchaseDate") as FormControl;
    return !purchaseDateControl.pristine;
  }

  hasExpirationDateChanged(index: number) {
    const expirationDateControl = this.dates.get(index.toString()).get("expirationDate") as FormControl;
    return !expirationDateControl.pristine;
  }

  hasSubscriptionBeenEdited(index: number) {
    return this.hasPurchaseDateChanged(index) || this.hasExpirationDateChanged(index);
  }

  resetPurchaseDate(index: number) {
    const purchaseDateControl = this.dates.get(index.toString()).get("purchaseDate") as FormControl;
    purchaseDateControl.reset();
    purchaseDateControl.setValue(this.data.userBuyings[index].purchaseDate);
  }

  resetExpirationDate(index: number) {
    const expirationDateControl = this.dates.get(index.toString()).get("expirationDate") as FormControl;
    expirationDateControl.reset();
    expirationDateControl.setValue(this.data.userBuyings[index].expirationDate);
  }

  updateUserBuyingDates(index: number) {
    const idControl = this.dates.get(index.toString()).get("id") as FormControl;
    const toUpdate = { ...this.data.userBuyings.filter(record => record.id === idControl.value)[0] };

    // console.log(toUpdate);
    const expirationDateControl = this.dates.get(index.toString()).get("expirationDate") as FormControl;
    toUpdate.expirationDate = Helpers.convertDateToLocalFormat(expirationDateControl.value);
    // console.log(Helpers.convertDateToLocalFormat(expirationDateControl.value));

    const purchaseDateControl = this.dates.get(index.toString()).get("purchaseDate") as FormControl;
    toUpdate.purchaseDate = Helpers.convertDateToLocalFormat(purchaseDateControl.value);
    // console.log(Helpers.convertDateToLocalFormat(purchaseDateControl.value));

    this.userBuyingsService.updateUserBuyingDates(toUpdate)
      .then(
        res => {
          // console.log(res);
          this.dialogService.openSuccessSnackBar('Abbonamento aggiornato correttamente');
          const indexOldSubscription = this.data.userBuyings.indexOf(this.data.userBuyings.filter(record => record.id === res.id)[0]);
          this.data.userBuyings[indexOldSubscription] = res;
          this.resetPurchaseDate(index);
          this.resetExpirationDate(index);
        },
        err => console.error('ERROR in UPDATE SUBSCRIPTION')
      );
  }

  openSubscriptionDialog(id: number) {
    const subscriptionName = this.data.userBuyings.filter(record => record.id === id)[0]?.productName;
    console.log(subscriptionName);
    this.dialogService.openDeleteSubscriptionConfirmationDialog(subscriptionName)
      .subscribe(result => {
        if (!result) {
          return;
        }
        this.deleteSubscription(id);
      });


  }
  deleteSubscription(id: number) {
    this.userBuyingsService.deleteUserBuyingDates(id)
      .then(
        res => {
          if (!res) {
            return;
          }
          const indexDeletedSubscription = this.data.userBuyings.indexOf(this.data.userBuyings.filter(record => record.id === id)[0]);
          this.data.userBuyings.splice(indexDeletedSubscription, 1);
          this.dialogService.openSuccessSnackBar('L\'Abbonamento è stato rimosso correttamente')
        },
        err => console.error(err)
      );
  }
}
